import { useEffect } from 'react';
import { useForecastMap, mapAlive } from '../ForecastMapContext';
import { windTileTemplate } from '../../../lib/wind/urls';
import { paramForLevel } from '../../../lib/wind/levels';
import { decodeUV, windSpeedDir, decodeScalar } from '../../../lib/wind/decode';
import { colorForSpeed, parsePaletteKey, scalarBandColor } from '../../../lib/wind/palette';
import { getDecodedTile } from './tileCache';
import { buildTerrainSampler, terrainMaskZoom } from '../../../lib/wind/terrainSampler';

const SOURCE_ID = 'wind:fill';
const LAYER_ID = 'wind:fill:raster';
const TILE = 256;          // render each tile's first 256×256 (drop the 1px overlap)
const FILL_OPACITY = 0.6;  // let the basemap/terrain show through

// ICON's composite dataset caps at z7 for every param (probed live; even
// uv_10, which has z9 on the SkySight model).
const maxZoomFor = (product, param, model) =>
  (model === 'ICON' ? 7 : product.maxTileZoom || (param === 'uv_10' ? 9 : 7));
const tileLng = (x, n) => (x / n) * 360 - 180;
const tileLat = (y, n) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;

const lngLatToTile = (lng, lat, z) => {
  const n = Math.pow(2, z);
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * n);
  return [Math.max(0, Math.min(n - 1, x)), Math.max(0, Math.min(n - 1, y))];
};

// Recolor one decoded tile's first 256×256 into an ImageData (speed→palette).
// fillMax = top of the colour scale in m/s (Skysight's uPmax).
function recolorTile(tile, range, fillMax, x, y, z, maskAbove) {
  const img = new ImageData(TILE, TILE);
  const out = img.data;
  const n = Math.pow(2, z);
  for (let py = 0; py < TILE; py++) {
    for (let px = 0; px < TILE; px++) {
      const si = (py * tile.w + px) * 4;
      const uv = decodeUV(
        tile.data[si] / 255, tile.data[si + 1] / 255, tile.data[si + 2] / 255, tile.data[si + 3] / 255,
        range.min, range.max,
      );
      const di = (py * TILE + px) * 4;
      if (!uv) { out[di + 3] = 0; continue; } // no data → transparent
      if (maskAbove) {
        const lng = ((x + px / TILE) / n) * 360 - 180;
        const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + py / TILE)) / n))) * 180) / Math.PI;
        if (maskAbove(lng, lat)) { out[di + 3] = 0; continue; } // above terrain → bare
      }
      const [r, g, b] = colorForSpeed(windSpeedDir(uv).speedMs, fillMax);
      out[di] = r; out[di + 1] = g; out[di + 2] = b; out[di + 3] = 255;
    }
  }
  return img;
}

// ---- Scalar variant (convergence/ridge) ----
// Value index in the RED channel, discrete isoband colours with per-band alpha
// (the neutral zone around 0 is transparent). Unlike the uv speed fill, banding
// at tile resolution then letting the GPU upscale smears the discrete bands into
// a continuous gradient (Skysight's shader bands per SCREEN pixel after
// filtering the field). So the scalar path renders a viewport-sized image:
// bilinear-sample the decoded value field per output pixel, THEN band.

const mercY = (lat) => (1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2;

// Decode a tile's red channel into a float grid (NaN = no data). Tiles are
// 257×258 — the +1 overlap row/col exists exactly so neighbours interpolate
// seamlessly across tile borders.
function scalarValueGrid(tile, range) {
  const g = new Float32Array(tile.w * tile.h);
  for (let i = 0; i < g.length; i++) {
    const v = decodeScalar(tile.data[i * 4] / 255, range.min, range.max);
    g[i] = v === null ? NaN : v;
  }
  return g;
}

// Bilinear sample with no-data handling: average the valid neighbours,
// reject the pixel when less than half the weight is valid (mirrors the
// shader's `valid < 0.5` rejection).
function sampleGrid(grid, w, fx, fy) {
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const dx = fx - x0, dy = fy - y0;
  let sum = 0, weight = 0;
  for (let j = 0; j <= 1; j++) {
    for (let i = 0; i <= 1; i++) {
      const v = grid[(y0 + j) * w + (x0 + i)];
      if (Number.isNaN(v)) continue;
      const wgt = (i ? dx : 1 - dx) * (j ? dy : 1 - dy);
      sum += v * wgt; weight += wgt;
    }
  }
  return weight < 0.5 ? NaN : sum / weight;
}

const WindSpeedFillLayer = ({ base, date, time, product, level, model }) => {
  const map = useForecastMap();
  const param = product && (product.levelStepped ? paramForLevel(level) : product.param);

  useEffect(() => {
    if (!map || !param || !date || !time || !product.fillPaletteKey) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const range = product.range;
    const scalar = product.kind === 'scalar';
    const fillMax = scalar ? null : product.fillRange.max;
    const scalarColors = scalar ? parsePaletteKey(product.fillPaletteKey) : null;
    const scalarScale = scalar ? { min: range.min, max: range.max, step: product.step } : null;
    const template = windTileTemplate({ base, date, time, param, model });
    // Terrain masking applies only to Wind-at-altitude (real altitude level).
    const maskLevel = product.levelStepped ? level : null;
    const gridCache = new Map(); // tile url -> Float32Array (scalar values)

    const tileGridXY = (z) => {
      const b = map.getBounds();
      const [x0, y0] = lngLatToTile(b.getWest(), b.getNorth(), z);
      const [x1, y1] = lngLatToTile(b.getEast(), b.getSouth(), z);
      return {
        xMin: Math.min(x0, x1), xMax: Math.max(x0, x1),
        yMin: Math.min(y0, y1), yMax: Math.max(y0, y1),
      };
    };

    const setImage = (canvas, coordinates) => {
      const url = canvas.toDataURL('image/png');
      const existing = map.getSource(SOURCE_ID);
      if (existing) {
        existing.updateImage({ url, coordinates });
      } else {
        map.addSource(SOURCE_ID, { type: 'image', url, coordinates });
        map.addLayer({
          id: LAYER_ID, type: 'raster', source: SOURCE_ID,
          paint: { 'raster-opacity': FILL_OPACITY, 'raster-resampling': 'linear', 'raster-fade-duration': 0 },
        }, map.getLayer('wind:barbs:symbols') ? 'wind:barbs:symbols' : undefined);
      }
    };

    // uv path: recolor each tile at native resolution into a tile-grid canvas
    // (continuous ramp — GPU linear upscale is invisible).
    const refreshUv = async (z) => {
      const n = Math.pow(2, z);
      const { xMin, xMax, yMin, yMax } = tileGridXY(z);
      const cols = xMax - xMin + 1, rows = yMax - yMin + 1;
      if (cols * rows > 24) return; // guard zoomed-out fan-out
      const canvas = document.createElement('canvas');
      canvas.width = cols * TILE; canvas.height = rows * TILE;
      const ctx = canvas.getContext('2d');
      let any = false;
      let maskAbove = null;
      if (maskLevel != null) {
        const b = map.getBounds();
        const sampler = await buildTerrainSampler({
          getTile: getDecodedTile, base, z: terrainMaskZoom(map.getZoom()),
          west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth(),
          signal: controller.signal,
        });
        if (cancelled) return;
        maskAbove = (lng, lat) => sampler.aboveLevel(lng, lat, maskLevel);
      }
      await Promise.all(
        Array.from({ length: cols * rows }, (_, k) => {
          const x = xMin + (k % cols), y = yMin + Math.floor(k / cols);
          const url = template.replace('{z}', z).replace('{x}', x).replace('{y}', y);
          return getDecodedTile(url, controller.signal)
            .then((tile) => {
              if (cancelled || !tile) return;
              ctx.putImageData(
                recolorTile(tile, range, fillMax, x, y, z, maskAbove),
                (x - xMin) * TILE, (y - yMin) * TILE,
              );
              any = true;
            })
            .catch(() => {});
        }),
      );
      if (cancelled || !any) return;
      setImage(canvas, [
        [tileLng(xMin, n), tileLat(yMin, n)],
        [tileLng(xMax + 1, n), tileLat(yMin, n)],
        [tileLng(xMax + 1, n), tileLat(yMax + 1, n)],
        [tileLng(xMin, n), tileLat(yMax + 1, n)],
      ]);
    };

    // scalar path: render a viewport-resolution image — bilinear-sample the
    // value field per output pixel, then band (crisp isobands like Skysight).
    const refreshScalar = async (z) => {
      const n = Math.pow(2, z);
      const { xMin, xMax, yMin, yMax } = tileGridXY(z);
      if ((xMax - xMin + 1) * (yMax - yMin + 1) > 24) return;
      const grids = new Map(); // 'x/y' -> { g, w }
      await Promise.all(
        Array.from({ length: (xMax - xMin + 1) * (yMax - yMin + 1) }, (_, k) => {
          const cols = xMax - xMin + 1;
          const x = xMin + (k % cols), y = yMin + Math.floor(k / cols);
          const url = template.replace('{z}', z).replace('{x}', x).replace('{y}', y);
          return getDecodedTile(url, controller.signal)
            .then((tile) => {
              if (cancelled || !tile) return;
              if (!gridCache.has(url)) gridCache.set(url, scalarValueGrid(tile, range));
              grids.set(`${x}/${y}`, { g: gridCache.get(url), w: tile.w, h: tile.h });
            })
            .catch(() => {});
        }),
      );
      if (cancelled || grids.size === 0) return;
      const container = map.getContainer();
      const W = Math.min(1600, Math.max(256, container.clientWidth || 800));
      const H = Math.min(1200, Math.max(256, container.clientHeight || 600));
      const b = map.getBounds();
      const west = b.getWest(), east = b.getEast(), north = b.getNorth(), south = b.getSouth();
      const tx0 = ((west + 180) / 360) * n, tx1 = ((east + 180) / 360) * n;
      const ty0 = mercY(north) * n, ty1 = mercY(south) * n;
      const img = new ImageData(W, H);
      const out = img.data;
      for (let py = 0; py < H; py++) {
        const ty = ty0 + ((ty1 - ty0) * (py + 0.5)) / H;
        const tileY = Math.floor(ty);
        const fy = (ty - tileY) * TILE;
        for (let px = 0; px < W; px++) {
          const tx = tx0 + ((tx1 - tx0) * (px + 0.5)) / W;
          const tileX = Math.floor(tx);
          const entry = grids.get(`${tileX}/${tileY}`);
          if (!entry) continue; // outside fetched tiles → transparent
          const v = sampleGrid(entry.g, entry.w, (tx - tileX) * TILE, fy);
          if (Number.isNaN(v)) continue;
          const [r, g, bC, a] = scalarBandColor(v, scalarScale, scalarColors);
          const di = (py * W + px) * 4;
          out[di] = r; out[di + 1] = g; out[di + 2] = bC; out[di + 3] = a;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      canvas.getContext('2d').putImageData(img, 0, 0);
      setImage(canvas, [[west, north], [east, north], [east, south], [west, south]]);
    };

    const refresh = async () => {
      if (cancelled) return;
      const z = Math.max(3, Math.min(maxZoomFor(product, param, model), Math.round(map.getZoom())));
      try {
        await (scalar ? refreshScalar(z) : refreshUv(z));
      } catch (e) { /* aborted or transient — ignore */ }
    };

    refresh();
    map.once('idle', refresh);
    map.on('moveend', refresh);

    return () => {
      cancelled = true;
      controller.abort();
      map.off('idle', refresh);
      map.off('moveend', refresh);
      if (!mapAlive(map)) return; // map already destroyed (route unmount)
      if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  }, [map, base, date, time, param, product, level, model]);

  return null;
};

export default WindSpeedFillLayer;
